/**
 * Shadow tokens - Enhanced elevation system
 * Provides depth and hierarchy through shadows with theme-aware colors
 */

/**
 * Convert hex color to rgba with opacity for shadows
 */
const hexToRgbaShadow = (hex: string, opacity: number): string => {
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.slice(0, 2), 16);
  const g = parseInt(cleanHex.slice(2, 4), 16);
  const b = parseInt(cleanHex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const shadows = {
  // Elevation levels (0-24) - Softer, more elegant shadows for museum aesthetic
  0: 'none',
  1: '0 1px 2px 0 rgba(0, 0, 0, 0.03)',
  2: '0 1px 3px 0 rgba(0, 0, 0, 0.04)',
  4: '0 2px 4px 0 rgba(0, 0, 0, 0.04)',
  6: '0 2px 8px 0 rgba(0, 0, 0, 0.06), 0 1px 3px 0 rgba(0, 0, 0, 0.04)',
  8: '0 4px 8px 0 rgba(0, 0, 0, 0.06), 0 2px 4px 0 rgba(0, 0, 0, 0.04)',
  12: '0 4px 16px 0 rgba(0, 0, 0, 0.08), 0 2px 6px 0 rgba(0, 0, 0, 0.04)',
  16: '0 8px 16px 0 rgba(0, 0, 0, 0.08), 0 4px 8px 0 rgba(0, 0, 0, 0.06)',
  24: '0 12px 32px 0 rgba(0, 0, 0, 0.12), 0 6px 12px 0 rgba(0, 0, 0, 0.08)',

  // Named elevation levels (backward compatibility)
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.04)',
  md: '0 2px 8px 0 rgba(0, 0, 0, 0.06), 0 1px 3px 0 rgba(0, 0, 0, 0.04)',
  lg: '0 4px 16px 0 rgba(0, 0, 0, 0.08), 0 2px 6px 0 rgba(0, 0, 0, 0.04)',
  xl: '0 8px 24px 0 rgba(0, 0, 0, 0.1), 0 4px 8px 0 rgba(0, 0, 0, 0.06)',
  '2xl': '0 12px 32px 0 rgba(0, 0, 0, 0.12), 0 6px 12px 0 rgba(0, 0, 0, 0.08)',

  // Colored shadows - Theme-aware colored shadows
  colored: {
    sm: (color: string) => {
      const rgba = color.startsWith('#') ? hexToRgbaShadow(color, 0.12) : color;
      return `0 1px 3px 0 ${rgba}`;
    },
    md: (color: string) => {
      const rgba = color.startsWith('#') ? hexToRgbaShadow(color, 0.15) : color;
      const rgba2 = color.startsWith('#') ? hexToRgbaShadow(color, 0.10) : color;
      return `0 2px 8px 0 ${rgba}, 0 1px 3px 0 ${rgba2}`;
    },
    lg: (color: string) => {
      const rgba = color.startsWith('#') ? hexToRgbaShadow(color, 0.18) : color;
      const rgba2 = color.startsWith('#') ? hexToRgbaShadow(color, 0.12) : color;
      return `0 4px 16px 0 ${rgba}, 0 2px 6px 0 ${rgba2}`;
    },
    xl: (color: string) => {
      const rgba = color.startsWith('#') ? hexToRgbaShadow(color, 0.20) : color;
      const rgba2 = color.startsWith('#') ? hexToRgbaShadow(color, 0.15) : color;
      return `0 8px 24px 0 ${rgba}, 0 4px 8px 0 ${rgba2}`;
    },
    '2xl': (color: string) => {
      const rgba = color.startsWith('#') ? hexToRgbaShadow(color, 0.25) : color;
      const rgba2 = color.startsWith('#') ? hexToRgbaShadow(color, 0.18) : color;
      return `0 12px 32px 0 ${rgba}, 0 6px 12px 0 ${rgba2}`;
    },
  },

  // Inner shadows for depth
  inner: {
    sm: 'inset 0 1px 2px 0 rgba(0, 0, 0, 0.04)',
    md: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
    lg: 'inset 0 4px 8px 0 rgba(0, 0, 0, 0.08)',
  },

  // Glow effects for interactive elements
  glow: {
    sm: (color: string) => {
      const rgba = color.startsWith('#') ? hexToRgbaShadow(color, 0.3) : color;
      return `0 0 8px 0 ${rgba}`;
    },
    md: (color: string) => {
      const rgba = color.startsWith('#') ? hexToRgbaShadow(color, 0.4) : color;
      return `0 0 16px 0 ${rgba}`;
    },
    lg: (color: string) => {
      const rgba = color.startsWith('#') ? hexToRgbaShadow(color, 0.5) : color;
      return `0 0 24px 0 ${rgba}`;
    },
  },

  // Hover state shadows
  hover: {
    sm: '0 2px 8px 0 rgba(0, 0, 0, 0.08), 0 1px 4px 0 rgba(0, 0, 0, 0.06)',
    md: '0 4px 16px 0 rgba(0, 0, 0, 0.12), 0 2px 8px 0 rgba(0, 0, 0, 0.08)',
    lg: '0 8px 24px 0 rgba(0, 0, 0, 0.16), 0 4px 12px 0 rgba(0, 0, 0, 0.12)',
  },

  // Focus ring shadows
  focus: (color: string) => {
    const rgba = color.startsWith('#') ? hexToRgbaShadow(color, 0.4) : color;
    return `0 0 0 3px ${rgba}`;
  },

  /** Editorial card shadow */
  card: '0 8px 24px rgba(20, 32, 44, 0.06)',
} as const;

export type Shadows = typeof shadows;
